import { CONTACT_URL } from "./constants";
import type {
	DocDetails,
	DocSummary,
	PostDetails,
	PostSummary,
} from "./content-types";
import { translations } from "./i18n";
import {
	type Locale,
	type LocaleSegment,
	localeToPathSegment,
	stripLocaleFromPathname,
} from "./i18n/locale";
import {
	buildCanonicalUrl,
	decodeHtmlEntities,
	SITE_NAME,
	SITE_URL,
	toExcerpt,
} from "./seo";
import { SITE_PAGES, type SitePage } from "./site-pages";

type AgentFormat = "markdown" | "text";

type LocaleItems<T> = {
	locale: Locale;
	items: T[];
};

type AgentResponseInput = {
	body: string;
	canonicalUrl: string;
	format: AgentFormat;
};

const CACHE_CONTROL = "public, max-age=300, s-maxage=3600";

const joinBlocks = (blocks: (string | null | undefined)[]) =>
	blocks
		.map((block) => block?.trim())
		.filter(Boolean)
		.join("\n\n")
		.concat("\n");

const stripHtmlPreservingLines = (value: string) =>
	decodeHtmlEntities(
		value
			.replace(/<br\s*\/?>/gi, "\n")
			.replace(
				/<\/(p|div|section|article|h[1-6]|li|ul|ol|blockquote|pre)>/gi,
				"\n",
			)
			.replace(/<[^>]*>/g, " "),
	);

const stripMarkdownToText = (value: string) => {
	const withoutCodeFences = value.replace(
		/```[^\n]*\n([\s\S]*?)```/g,
		(_, code: string) => `\n${code.trim()}\n`,
	);
	const withExpandedImages = withoutCodeFences.replace(
		/!\[([^\]]*)\]\(([^)]+)\)/g,
		(_, alt: string, url: string) => (alt ? `${alt}: ${url}` : url),
	);
	const withExpandedLinks = withExpandedImages.replace(
		/\[([^\]]+)\]\(([^)]+)\)/g,
		(_, label: string, url: string) => `${label}: ${url}`,
	);
	const withoutMarkdown = withExpandedLinks
		.replace(/^\s*#{1,6}\s+/gm, "")
		.replace(/^>\s?/gm, "")
		.replace(/^\s*[-*+]\s+/gm, "")
		.replace(/^\s*\d+\.\s+/gm, "")
		.replace(/[*_~`]/g, "");

	return stripHtmlPreservingLines(withoutMarkdown)
		.split("\n")
		.map((line) => line.replace(/[ \t]+/g, " ").trim())
		.filter(Boolean)
		.join("\n");
};

const metadataMarkdown = (items: Record<string, string | null | undefined>) =>
	Object.entries(items)
		.filter(([, value]) => value)
		.map(([label, value]) => `${label}: ${value}`)
		.join("\n");

const metadataText = metadataMarkdown;

export const contentLocaleToLocale = (contentLocale: string): Locale | null => {
	if (contentLocale === "fr") return "fr-FR";
	if (contentLocale === "en") return "en-US";
	return null;
};

export const getCanonicalPageUrl = (path: string, locale: Locale) =>
	buildCanonicalUrl(path, locale);

export const getAgentContentUrls = (path: string, locale: Locale) => {
	const segment = localeToPathSegment(locale);
	const cleanPath = path.replace(/^\/+|\/+$/g, "");
	const contentPath =
		cleanPath === ""
			? "home"
			: cleanPath
					.split("/")
					.map((part) => encodeURIComponent(part))
					.join("/");

	return {
		markdownUrl: `${SITE_URL}/content/${segment}/${contentPath}.md`,
		textUrl: `${SITE_URL}/content/${segment}/${contentPath}.txt`,
	};
};

export const getAgentReadableRedirectPath = (
	pathname: string,
	locale: Locale,
) => {
	const contentPath = stripLocaleFromPathname(pathname);
	const match = contentPath.match(/^\/(blog|docs)\/(.+)\.(md|txt)$/);
	if (!match) return null;

	const [, section, slug, extension] = match;
	if (!section || !slug || !extension) return null;

	return `/content/${localeToPathSegment(locale)}/${section}/${encodeURIComponent(
		slug,
	)}.${extension}`;
};

export const buildAgentContentResponse = ({
	body,
	canonicalUrl,
	format,
}: AgentResponseInput) =>
	new Response(body, {
		headers: {
			"Cache-Control": CACHE_CONTROL,
			"Content-Type":
				format === "markdown"
					? "text/markdown; charset=utf-8"
					: "text/plain; charset=utf-8",
			Link: `<${canonicalUrl}>; rel="canonical"`,
			"X-Robots-Tag": "noindex, follow",
		},
	});

type HomeBlock = { title: string; intro?: string; items: string[] };

/** Home page content in reading order, shared by the Markdown and text renderers. */
const homeBlocks = (locale: Locale): HomeBlock[] => {
	const home = translations[locale].home;

	return [
		{
			title: home.services.title,
			intro: home.services.description,
			items: Object.values(home.services.items).map(
				(item) => `${item.eyebrow}: ${item.title} ${item.description}`,
			),
		},
		{
			title: home.ai.title,
			intro: home.ai.description,
			items: [
				...Object.values(translations[locale].economics.method).map(
					(item) => `${item.title}: ${item.description}`,
				),
				`${home.ai.saas.title} ${home.ai.saas.description}`,
			],
		},
		{
			title: home.work.title,
			intro: home.work.description,
			items: Object.values(home.work.facts).map(
				(fact) => `${fact.label}: ${fact.value}`,
			),
		},
		{
			title: home.process.title,
			intro: home.process.description,
			items: Object.values(home.process.steps).map(
				(step) => `${step.title}: ${step.description}`,
			),
		},
		{ title: home.company.title, items: [home.company.description] },
		{
			title: home.faq.title,
			items: Object.values(home.faq.items).map(
				(item) => `${item.question} ${item.answer}`,
			),
		},
		{ title: home.cta.title, items: [home.cta.description] },
	];
};

const homeLinks = (locale: Locale) => [
	...(Object.keys(SITE_PAGES) as SitePage[]).map((page) => ({
		label: translations[locale].seo.pages[page].title,
		url: getCanonicalPageUrl(SITE_PAGES[page], locale),
	})),
	{ label: "Email", url: CONTACT_URL },
];

export const renderHomeMarkdown = (locale: Locale) => {
	const hero = translations[locale].home.hero;
	const canonicalUrl = getCanonicalPageUrl("/", locale);

	return joinBlocks([
		`# ${hero.title}`,
		metadataMarkdown({
			Type: "homepage",
			Language: locale,
			"Canonical URL": canonicalUrl,
		}),
		hero.intro,
		...homeBlocks(locale).map((block) =>
			joinBlocks([
				`## ${block.title}`,
				block.intro,
				block.items.map((item) => `- ${item}`).join("\n"),
			]).trim(),
		),
		[
			"## Related links",
			...homeLinks(locale).map((link) => `- [${link.label}](${link.url})`),
		].join("\n"),
	]);
};

export const renderHomeText = (locale: Locale) => {
	const hero = translations[locale].home.hero;
	const canonicalUrl = getCanonicalPageUrl("/", locale);

	return joinBlocks([
		hero.title,
		metadataText({
			Type: "homepage",
			Language: locale,
			"Canonical URL": canonicalUrl,
		}),
		hero.intro,
		...homeBlocks(locale).map((block) =>
			joinBlocks([block.title, block.intro, block.items.join("\n")]).trim(),
		),
		[
			"Related links",
			...homeLinks(locale).map((link) => `${link.label}: ${link.url}`),
		].join("\n"),
	]);
};

export const renderPostMarkdown = (post: PostDetails, locale: Locale) => {
	const canonicalUrl = getCanonicalPageUrl(`/blog/${post.slug}`, locale);
	const summary = toExcerpt(stripMarkdownToText(post.agentContent));

	return joinBlocks([
		`# ${post.title}`,
		metadataMarkdown({
			Type: "blog post",
			Language: locale,
			"Canonical URL": canonicalUrl,
			Published: post.publishedAtLabel,
			Modified: post.updatedAt ?? undefined,
			Summary: summary,
		}),
		post.agentContent,
		[
			"## Related links",
			`- [Blog index](${getCanonicalPageUrl("/blog", locale)})`,
			`- [Website](${getCanonicalPageUrl("/", locale)})`,
		].join("\n"),
	]);
};

export const renderPostText = (post: PostDetails, locale: Locale) => {
	const canonicalUrl = getCanonicalPageUrl(`/blog/${post.slug}`, locale);
	const content = stripMarkdownToText(post.agentContent);
	const summary = toExcerpt(content);

	return joinBlocks([
		post.title,
		metadataText({
			Type: "blog post",
			Language: locale,
			"Canonical URL": canonicalUrl,
			Published: post.publishedAtLabel,
			Modified: post.updatedAt ?? undefined,
			Summary: summary,
		}),
		content,
		[
			"Related links",
			`Blog index: ${getCanonicalPageUrl("/blog", locale)}`,
			`Website: ${getCanonicalPageUrl("/", locale)}`,
		].join("\n"),
	]);
};

export const renderDocMarkdown = (doc: DocDetails, locale: Locale) => {
	const canonicalUrl = getCanonicalPageUrl(`/docs/${doc.slug}`, locale);
	const summary = toExcerpt(stripMarkdownToText(doc.agentContent));

	return joinBlocks([
		`# ${doc.title}`,
		metadataMarkdown({
			Type: "document",
			Language: locale,
			"Canonical URL": canonicalUrl,
			Updated: doc.updatedAtLabel,
			Summary: summary,
		}),
		doc.agentContent,
		[
			"## Related links",
			`- [Documents index](${getCanonicalPageUrl("/docs", locale)})`,
			`- [Website](${getCanonicalPageUrl("/", locale)})`,
		].join("\n"),
	]);
};

export const renderDocText = (doc: DocDetails, locale: Locale) => {
	const canonicalUrl = getCanonicalPageUrl(`/docs/${doc.slug}`, locale);
	const content = stripMarkdownToText(doc.agentContent);
	const summary = toExcerpt(content);

	return joinBlocks([
		doc.title,
		metadataText({
			Type: "document",
			Language: locale,
			"Canonical URL": canonicalUrl,
			Updated: doc.updatedAtLabel,
			Summary: summary,
		}),
		content,
		[
			"Related links",
			`Documents index: ${getCanonicalPageUrl("/docs", locale)}`,
			`Website: ${getCanonicalPageUrl("/", locale)}`,
		].join("\n"),
	]);
};

const localeLabel = (locale: Locale) =>
	locale === "fr-FR" ? "French" : "English";

const markdownContentLink = (label: string, url: string, description: string) =>
	`- [${label}](${url}): ${description}`;

const pushOptionalTextLinks = <T extends { slug?: string; title?: string }>(
	lines: string[],
	itemsByLocale: LocaleItems<T>[],
	kind: "blog" | "docs",
) => {
	for (const { locale, items } of itemsByLocale) {
		for (const item of items) {
			if (!item.slug || !item.title) continue;
			const urls = getAgentContentUrls(`/${kind}/${item.slug}`, locale);
			lines.push(
				markdownContentLink(
					`${item.title} (${localeLabel(locale)}, plain text)`,
					urls.textUrl,
					"Plain-text fallback.",
				),
			);
		}
	}
};

export const renderLlmsTxt = ({
	postsByLocale,
	docsByLocale,
}: {
	postsByLocale: LocaleItems<PostSummary>[];
	docsByLocale: LocaleItems<DocSummary>[];
}) => {
	const coreLinks: string[] = [];
	const optionalLinks: string[] = [];

	for (const locale of ["fr-FR", "en-US"] satisfies Locale[]) {
		const segment = localeToPathSegment(locale);
		const homeUrls = getAgentContentUrls("/", locale);
		coreLinks.push(
			markdownContentLink(
				`Homepage (${localeLabel(locale)})`,
				homeUrls.markdownUrl,
				"Positioning, services, proof and contact.",
			),
		);
		optionalLinks.push(
			markdownContentLink(
				`Homepage (${localeLabel(locale)}, plain text)`,
				homeUrls.textUrl,
				"Plain-text fallback.",
			),
		);
		for (const page of Object.keys(SITE_PAGES) as SitePage[]) {
			const copy = translations[locale].seo.pages[page];
			coreLinks.push(
				markdownContentLink(
					`${copy.title} (${segment})`,
					getCanonicalPageUrl(SITE_PAGES[page], locale),
					copy.description,
				),
			);
		}
	}

	const blogLinks = postsByLocale.flatMap(({ locale, items }) =>
		items.map((post) => {
			const urls = getAgentContentUrls(`/blog/${post.slug}`, locale);
			return markdownContentLink(
				`${post.title} (${localeLabel(locale)})`,
				urls.markdownUrl,
				`Blog post published ${post.publishedAtLabel}.`,
			);
		}),
	);

	const docLinks = docsByLocale.flatMap(({ locale, items }) =>
		items.map((doc) => {
			const urls = getAgentContentUrls(`/docs/${doc.slug}`, locale);
			return markdownContentLink(
				`${doc.title} (${localeLabel(locale)})`,
				urls.markdownUrl,
				"Public legal document.",
			);
		}),
	);

	pushOptionalTextLinks(optionalLinks, postsByLocale, "blog");
	pushOptionalTextLinks(optionalLinks, docsByLocale, "docs");

	return joinBlocks([
		`# ${SITE_NAME}`,
		`> ${SITE_NAME} is a software development and publishing company: custom business applications, React Native to Expo migration, and its own products such as Questovery.`,
		"Use the Markdown URLs first. Plain-text URLs are provided as optional fallbacks for agents or crawlers that prefer unformatted text.",
		["## Core pages", ...coreLinks].join("\n"),
		blogLinks.length ? ["## Blog posts", ...blogLinks].join("\n") : null,
		docLinks.length ? ["## Documents", ...docLinks].join("\n") : null,
		[
			"## Optional",
			...optionalLinks,
			markdownContentLink(
				"Sitemap",
				`${SITE_URL}/sitemap.xml`,
				"Canonical XML sitemap for search crawlers.",
			),
		].join("\n"),
	]);
};

export const renderLlmsFullMarkdown = ({
	postsByLocale,
	docsByLocale,
}: {
	postsByLocale: LocaleItems<PostDetails>[];
	docsByLocale: LocaleItems<DocDetails>[];
}) =>
	joinBlocks([
		`# ${SITE_NAME} full agent context`,
		"> Aggregated public Markdown content for agents. HTML pages remain canonical.",
		renderHomeMarkdown("fr-FR"),
		renderHomeMarkdown("en-US"),
		...postsByLocale.flatMap(({ locale, items }) =>
			items.map((post) => renderPostMarkdown(post, locale)),
		),
		...docsByLocale.flatMap(({ locale, items }) =>
			items.map((doc) => renderDocMarkdown(doc, locale)),
		),
	]);

export const localeSegments = ["fr", "en"] satisfies LocaleSegment[];
